import { Box, Skeleton, useMediaQuery, IconButton, useTheme } from '@mui/material';
import { useContext, useState, useMemo, useRef, useEffect } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Context } from '@/context';
import { REDUCERS } from '@/constants/reducers';
import StoryItem from './StoryItem';
import StoryModal from './StoryModal';

const STORIES_VIEWED_KEY = 'analitica_fantasy_stories_viewed';

const saveViewedStory = (storyId) => {
    if (typeof window === 'undefined') return;
    try {
        const viewed = localStorage.getItem(STORIES_VIEWED_KEY);
        const viewedArray = viewed ? JSON.parse(viewed) : [];
        if (!viewedArray.includes(storyId)) {
            viewedArray.push(storyId);
            localStorage.setItem(STORIES_VIEWED_KEY, JSON.stringify(viewedArray));
        }
    } catch {
        // Silently fail
    }
};

const StoriesLayout = () => {
    const { state, dispatch } = useContext(Context);
    const theme = useTheme();
    const scrollContainerRef = useRef(null);
    const [selectedStory, setSelectedStory] = useState(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    const isMobile = useMediaQuery('(max-width: 600px)');
    const isDesktop = useMediaQuery('(min-width: 500px)');
    const size = isMobile ? 40 : 50;

    const stories = state.stories || [];

    const scroll = (direction) => {
        const scrollAmount = 200;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
        }
    };

    const checkOverflow = () => {
        if (scrollContainerRef.current && isDesktop) {
            const hasOverflow =
                scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;
            setShowScrollButtons(hasOverflow);
        } else {
            setShowScrollButtons(false);
        }
    };

    const markStoryAsViewed = (storyId) => {
        if (!storyId) return;
        // Guardar en localStorage
        saveViewedStory(storyId);
        // Actualizar el state mediante dispatch
        dispatch({
            type: REDUCERS.MARK_STORY_AS_VIEWED,
            payload: storyId,
        });
    };

    const handleStoryClick = (storyId) => {
        const story = stories.find((s) => s.id === storyId);

        if (!story) return;

        if (!story.isViewed) {
            markStoryAsViewed(storyId);
        }
        setSelectedStory(story);
    };

    const handleCloseModal = () => {
        setSelectedStory(null);
    };

    const handleNextStory = () => {
        if (!selectedStory) return;

        const currentIndex = stories.findIndex((s) => s.id === selectedStory.id);
        if (currentIndex !== -1 && currentIndex < stories.length - 1) {
            const nextStory = stories[currentIndex + 1];
            if (nextStory?.id && !nextStory.isViewed) {
                markStoryAsViewed(nextStory.id);
            }
            setSelectedStory(nextStory);
        }
    };

    const handlePreviousStory = () => {
        if (!selectedStory) return;

        const currentIndex = stories.findIndex((s) => s.id === selectedStory.id);
        if (currentIndex !== -1 && currentIndex > 0) {
            const prevStory = stories[currentIndex - 1];
            if (prevStory?.id && !prevStory.isViewed) {
                markStoryAsViewed(prevStory.id);
            }
            setSelectedStory(prevStory);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            checkOverflow();
        };

        checkOverflow();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [stories, isDesktop]);

    const showSkeletons = stories.length === 0;

    return (
        <>
            <Box
                className="container"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    position: 'relative',
                }}
            >
                {/* Flecha izquierda */}
                {showScrollButtons && isDesktop && (
                    <IconButton
                        onClick={() => scroll(-1)}
                        sx={{
                            width: 32,
                            height: 32,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.primary,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${theme.palette.border.main}`,
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                            marginRight: '8px',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                transform: 'scale(1.05)',
                            },
                            '&:active': {
                                transform: 'scale(0.95)',
                            },
                        }}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                )}

                {/* Scroll horizontal de stories */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.5,
                        overflowX: isDesktop ? 'hidden' : 'auto',
                        overflowY: 'hidden',
                        flex: 1,
                        minWidth: 0,
                        paddingLeft: { xs: '5px', md: showScrollButtons ? '8px' : '15px' },
                        paddingRight: { xs: '5px', md: showScrollButtons ? '8px' : '15px' },
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {showSkeletons
                        ? // Mostrar 20 skeletons mientras se cargan las stories
                          Array.from({ length: 20 }).map((_, index) => (
                              <Box
                                  key={`skeleton-${index}`}
                                  sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      minWidth: isMobile ? 40 : 50,
                                  }}
                              >
                                  <Skeleton
                                      variant="circular"
                                      width={size}
                                      height={size}
                                      sx={{
                                          flexShrink: 0,
                                      }}
                                  />
                              </Box>
                          ))
                        : stories.map((story, index) => {
                              return (
                                  <StoryItem
                                      key={story?.id || `story-${index}`}
                                      story={story}
                                      isViewed={story.isViewed || false}
                                      onClick={() => handleStoryClick(story?.id)}
                                      size={size}
                                  />
                              );
                          })}
                </Box>

                {/* Flecha derecha */}
                {showScrollButtons && isDesktop && (
                    <IconButton
                        onClick={() => scroll(1)}
                        sx={{
                            width: 32,
                            height: 32,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.text.primary,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${theme.palette.border.main}`,
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                            marginLeft: '8px',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                transform: 'scale(1.05)',
                            },
                            '&:active': {
                                transform: 'scale(0.95)',
                            },
                        }}
                    >
                        <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                )}
            </Box>

            {selectedStory && (
                <StoryModal
                    open={!!selectedStory}
                    story={selectedStory}
                    onClose={handleCloseModal}
                    onNext={handleNextStory}
                    onPrevious={handlePreviousStory}
                    hasNext={stories.findIndex((s) => s.id === selectedStory.id) < stories.length - 1}
                    hasPrevious={stories.findIndex((s) => s.id === selectedStory.id) > 0}
                />
            )}
        </>
    );
};

export default StoriesLayout;
